import React, { useState } from "react";
import { Button, Input, message, Modal, Rate } from "antd";
import { useModalStore } from "@/hooks/useModalStore";
import { PlatformIconComponent } from "@/biz/platform";
import normalizePrice from "@/utils/normalizePrice";
import { useTranslation } from "react-i18next";
import { reviewOrderApi, editReviewOrderApi } from "@/api/order";

export default function RateModal() {
  const { t } = useTranslation();
  const { rateModalInfo, setRateModalInfo } = useModalStore();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const handleSubmit = async () => {
    try {
      setLoading(true);
      const { rate, comment } = rateModalInfo;
      if (!rate) {
        setError(true);
        return;
      }
      setError(false);
      await (rateModalInfo?.isEdit ? editReviewOrderApi : reviewOrderApi)(rateModalInfo.orderId, {
        rating: rate,
        comment: comment,
      });
      if (typeof rateModalInfo?.updateFunc === "function") {
        await rateModalInfo.updateFunc();
      }
      handleCancel();
    } catch (error) {
      console.log(error);
      message.error(`${error}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setRateModalInfo({
      visible: false,
      influencerInfo: {
        creatorName: "",
        creatorAvatarUrl: "",
        platform: "",
        price: 0,
      },
      rate: 0,
      comment: "",
      orderId: "",
      updateFunc: () => {},
    });
  };

  return (
    <Modal
      open={rateModalInfo.visible}
      onCancel={handleCancel}
      footer={null}
      title={t("components.rateModal.title")}
    >
      <div className="pt-2 w-full flex flex-col gap-6">
        <div className="justify-start text-font&icon-secondary text-sm font-normal font-['DM_Sans'] leading-snug">
          {t("components.rateModal.description")}
        </div>
        <div className="border border-divideColor bg-[#FAFAF9] rounded-lg p-4 flex gap-4">
          <div className="w-14 h-14 flex relative flex-shrink-0">
            <img
              alt=""
              className="w-14 h-14 rounded-full"
              src={rateModalInfo?.influencerInfo?.creatorAvatarUrl}
            />
            <div className="absolute right-0 bottom-0">
              <PlatformIconComponent
                platform={rateModalInfo?.influencerInfo?.platform}
                className="w-[20px] h-[20px]"
              />
            </div>
          </div>
          <div className="inline-flex flex-col justify-center items-start gap-px">
            <div className="justify-start text-Zinc-950 text-base font-semibold font-['DM_Sans'] leading-normal">
              {rateModalInfo?.influencerInfo?.creatorName}
            </div>
            <div className="flex justify-start items-center gap-6">
              <div className="flex justify-start items-center gap-2">
                <div className="text-font&icon-secondary text-sm font-medium font-['DM_Sans'] leading-snug">
                  {t("components.rateModal.budgetConsumed")}:
                </div>
                <div className="justify-start text-font&icon-primary text-base font-semibold font-['DM_Sans'] leading-normal">
                  {normalizePrice(rateModalInfo?.influencerInfo?.price)}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <div className="flex justify-start items-center gap-px">
            <div className="justify-start text-font&icon-secondary text-sm font-medium font-['DM_Sans'] leading-snug">
              {t("components.rateModal.rating")}
            </div>
            <div className="justify-start text-font&icon-error text-sm font-medium font-['DM_Sans'] leading-snug">
              *
            </div>
          </div>
          <div className="flex items-center gap-[6px]">
            <Rate
              allowHalf
              className="text-primary"
              value={rateModalInfo?.rate}
              onChange={(value) => {
                setError(false);
                setRateModalInfo({ ...rateModalInfo, rate: value });
              }}
            />
            {!!rateModalInfo?.rate && (
              <div className="justify-center text-neutral-800 text-xl font-bold font-['DM_Sans']">
                {rateModalInfo?.rate}
              </div>
            )}
          </div>
          {error && (
            <div className="justify-start text-font&icon-error text-sm font-medium font-['DM_Sans'] leading-snug">
              <span>{t("components.rateModal.pleaseSelectRating")}</span>
            </div>
          )}
        </div>
        <div className="flex flex-col gap-2">
          <div className="justify-start text-font&icon-secondary text-sm font-medium font-['DM_Sans'] leading-snug">
            {t("components.rateModal.review")}
          </div>
          <Input.TextArea
            value={rateModalInfo?.comment}
            onChange={(e) =>
              setRateModalInfo({ ...rateModalInfo, comment: e.target.value })
            }
            autoSize={{ minRows: 3, maxRows: 5 }}
          />
        </div>
        <div className="flex items-center gap-4 w-full">
          <Button
            type="default"
            size="large"
            className="w-[120px]"
            onClick={handleCancel}
          >
            {t("components.rateModal.cancel")}
          </Button>
          <Button
            type="primary"
            size="large"
            className="flex-1"
            onClick={handleSubmit}
            loading={loading}
          >
            {t("components.rateModal.submitRating")}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
